import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";
import styles from "./Goals.module.scss";
import { useAppSelector } from "../../../../redux/hooks";
import { RootState } from "../../../../redux/store";
import Button from "../../../common/Button/Button";
import {
  useUpdateGoalsDataMutation,
  useGetGoalsFullDataQuery,
  useCreateGoalMutation,
} from "../../../../redux/services/goalsAPI";
import FormatterBar from "../../../common/FormatterBar/FormatterBar";
import HTMLtoText from "../../../common/HTMLToText/HTMLToText";
import ABOBackgroundLoader from "../../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader";
import ErrorInPageScreen from "../../../common/ErrorInPageScreen/ErrorInPageScreen";
import { isValidResponse } from "../../../../utils/validateAPIResponse";
import { useProfileParams } from "../../../../utils/common";
import { ReactComponent as EmptyGoalsLightImg } from "../../../../assets/images/LightTheme/no_connection_light.svg";
import { ReactComponent as EmptyGoalsDarkImg } from "../../../../assets/images/DarkTheme/no_connection_dark.svg";
import TextButton from "../../../common/TextButton/TextButton";
import ViewGoals from "../ViewGoals/ViewGoals";
import Modal from "../../../common/Modal/Modal";
import AddGoalModal from "./AddGoalModal/AddGoalModal";
import { disableEnableScroll } from "../../../../utils/disableEnableScroll";
import Icon from "../../../common/Icon/Icon";

type GoalsProp = {
  selectedHistoryData: HistoryItem | null,
  showDetailsPage: boolean;
  setShowDetailsPage: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedHistoryData: React.Dispatch<React.SetStateAction<HistoryItem | null>>;
}
const Goals: React.FC<GoalsProp> = ({ selectedHistoryData, setSelectedHistoryData, showDetailsPage, setShowDetailsPage }) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const [isEditable, setIsEditable] = useState(false);
  const [isEditEnable, setIsEditEnable] = useState(true);
  const [goalsData, setGoalsData] = useState<GoalData[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);

  // const [updatedGoalsData, setUpdatedGoalsData] = useState<GoalData>([]);
  const [isAPIError, setIsAPIError] = useState(false);

  const [showViewGoals, setShowViewGoals] = useState(false);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGoalIndex, setSelectedGoalIndex] = useState<number | null>(
    null
  ); // [TODO: Please remove the index and use the ID once the API is implemented.]

  // [Note: Commented This Code As API is not implemented yet.]
  const {
    data: goalsResponseData,
    isFetching: isLoadingGetGoals,
    isError: isFetchGoalsError,
  } = useGetGoalsFullDataQuery(
    {
      affiliateCode,
      aboNumber,
    },
    { refetchOnMountOrArgChange: true, skip: isAPIError }
  );

  const [
    createGoals,
    {
      isLoading: isLoadingCreateGoals,
      isError: isCreateGoalError,
    }] = useCreateGoalMutation();

  const [
    updateGoalsData,
    { isLoading: isLoadingUpdateGoals, isError: isErrorInSave },
  ] = useUpdateGoalsDataMutation();

  const handleAPIError = () => {
    setIsAPIError(true);
    setGoalsData([]);
    // setUpdatedGoalsData("");
  };

  // [Note: Commented This Code As API is not implemented yet.]
  useEffect(() => {
    if (isFetchGoalsError || isErrorInSave) handleAPIError();
  }, [isFetchGoalsError, isErrorInSave]);

  useEffect(() => {
    if (!isEmpty(goalsResponseData)) {
      if (isValidResponse(goalsResponseData?.statusCode)) {
        setGoalsData(!isEmpty(goalsResponseData?.body) ? goalsResponseData?.body : []);
      } else {
        handleAPIError();
      }
    }
  }, [goalsResponseData, isLoadingGetGoals]);

  const handleClickEdit = () => {
    setIsEditable(true);
  };

  const handleClickAddGoal = () => {
    setModalOpen(true);
  };
  const handleModalClose = () => {
    setModalOpen(false);
  };
  useEffect(() => {
    disableEnableScroll(modalOpen);
  }, [modalOpen]);

  const handleClickCancel = () => {
    setIsEditable(false);
    // setUpdatedGoalsData(goalsData);
  };

  const EmptyGoalsImage =
    currentTheme === "light" ? EmptyGoalsLightImg : EmptyGoalsDarkImg;

  const emptyGoalsRender = () => (
    <div className={`empty_goals ${styles.empty_goals}`}>
      <EmptyGoalsImage height={150} />
      <div className={`empty_goals_text ${styles.empty_goals_text}`}>
        <span>{t("noGoalsMsg")}</span>
        <TextButton
          label={t("addGoal")}
          onClick={handleClickAddGoal}
          data-testid="add_goal_testID"
        />
      </div>
    </div>
  );
  const visibleGoals = !showDetailsPage
    ? goalsData
    : goalsData?.filter((goals) => goals?.aboGoalId === selectedGoalId);

  return (
    <div
      className={`goals_wrapper ${styles.goals_wrapper} ${isAPIError ? styles.full_width : ""}`}
    >
      {/* [Note: Commented This Code As API is not implemented yet.] */}
      {isLoadingGetGoals || isLoadingUpdateGoals || isLoadingCreateGoals ? (
        <ABOBackgroundLoader num={1} heading={t("goals")} />
      ) :
        isAPIError ? (
          <>
            <div className={styles.heading_area}>
              <span
                className={styles.heading}
                data-testid="goals_bg_heading_testID"
              >
                {t("goals")}
              </span>
            </div>
            <ErrorInPageScreen
              handleClickTryAgain={() => {
                setIsAPIError(false);
              }}
            />
          </>
        ) : (
          <>
            {/* Heading with edit icon */}
            {showDetailsPage ? (
              <span
                className={styles.details_page_heaading}
                data-testid="goals_details_heading_testID"
                onClick={() => {
                  setSelectedHistoryData(null)
                  setShowDetailsPage((prev) => !prev)
                }}
              >
                <Icon
                  name="chevron_left_icon"
                  size={13}
                  className={styles.circle_icon}
                  color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
                />
                <span className={styles.details_heading_text}>
                  {t("back_to_list")}
                </span>
              </span>
            ) : (
              <div className={styles.heading_area}>
                <span
                  className={styles.heading}
                  data-testid="goals_bg_heading_testID"
                >
                  {t("goals")}
                </span>
                <span className={styles.right_section}>
                  {!isEmpty(goalsData) && (
                    <TextButton
                      label={t("addGoal")}
                      onClick={handleClickAddGoal}
                      data-testid="add_goal_testID"
                      isIcon={true}
                      iconName="plus_big"
                      iconSize={16}
                      themeColor={currentTheme === "light" ? "#000" : "#fff"}
                    />
                  )}
                  {/* Commented Older Goals Edit Button */}
                  {/* {!isEditable && !isEmptyGoals && (
                  <TextButton
                    label={t("edit")}
                    onClick={handleClickEdit}
                    data-testid="edit_testID"
                  />
                )} */}
                </span>
              </div>
            )}
            <Modal showDrawer={modalOpen} onClose={handleModalClose}>
              <AddGoalModal
                onClose={handleModalClose}
                onSave={(goal) => {
                  const goalPayload = {
                    ...(goal?.aboGoalId) && { aboGoalId: goal?.aboGoalId },
                    title: goal?.goalTitle,
                    note: goal?.note,
                    timeframeType: goal.timeFrame,
                    kpiCodes: goal.kpiCodes.map((metric: any) => metric.code) || [],
                    outcomeType: goal?.outcome,
                    outcomeDetail: {
                      incomeType: goal?.incomeType,
                      incomeValue: +goal?.monthlyIncome,
                      awardCode: goal?.awardType?.code,
                      other: (goal?.outcome === "9" || goal?.outcome === 9) ? "other description" : ""
                    }
                  }

                  createGoals({
                    affiliateCode,
                    aboNumber,
                    payload: goalPayload
                  }).then((resp: any) => {
                    const goalRestData = resp?.data?.body;
                    if (!isEmpty(resp?.data?.body)) {
                      setGoalsData((prevGoals) => {
                        const exists = prevGoals.some((goal) => goal?.aboGoalId === goalRestData?.aboGoalId)
                        if (exists) {
                          return prevGoals.map(goalItem =>
                            goalItem?.aboGoalId === goalRestData?.aboGoalId ? { ...goalItem, ...goalRestData } : goalItem
                          );
                        } else {
                          return [...prevGoals, goalRestData];
                        }
                      });
                    }
                  })

                  setShowViewGoals(true);
                  setModalOpen(false);
                }}
              />
            </Modal>
            {(!showViewGoals && isEmpty(goalsData) && isEmpty(selectedHistoryData)) ? (
              <div className={`goals_section ${styles.goals_section}`}>
                {emptyGoalsRender()}
                { }
              </div>
            ) : (
              !isEmpty(selectedHistoryData) ? (
                <ViewGoals
                  key={selectedHistoryData?.aboGoalId}
                  goalData={selectedHistoryData}
                  setShowDetailsPage={setShowDetailsPage}
                  showDetailsPage={showDetailsPage}
                  selectedGoalId={selectedGoalId}
                  setSelectedGoalId={setSelectedGoalId}
                  selectedHistoryData={selectedHistoryData}
                  onGoalSave={(updatedGoal) => {
                    setSelectedHistoryData((prev) => {
                      if (!prev) return prev;
                      if (prev.aboGoalId !== updatedGoal.aboGoalId) return prev;
                      return { ...prev, ...updatedGoal } as HistoryItem;
                    });
                  }}
                />
              ) : (
                visibleGoals?.map((data: GoalData, index: number) => (
                  <ViewGoals
                    key={data?.aboGoalId}
                    goalData={data}
                    onGoalSave={(updatedGoal) => {

                      updateGoalsData({
                        affiliateCode,
                        aboNumber,
                        payload: updatedGoal
                      }).then((resp: any) => {
                        const updatedGoalRespData = resp?.data?.body;
                        if (!isEmpty(resp?.data?.body) && resp?.data?.statusCode === 200) {
                          setGoalsData((prevGoals: any) => {
                            const exists = prevGoals?.some((goal: any) => goal?.aboGoalId === updatedGoalRespData?.aboGoalId)
                            if (exists) {
                              return prevGoals.map((goalItem: any) => {
                                const comparison = goalItem?.aboGoalId === updatedGoalRespData?.aboGoalId ? { ...goalItem, ...updatedGoalRespData } : goalItem;
                                return comparison;
                              }

                              );
                            }
                          }
                          );
                        }
                      })
                    }}
                    setShowDetailsPage={setShowDetailsPage}
                    showDetailsPage={showDetailsPage}
                    selectedGoalId={selectedGoalId}
                    setSelectedGoalId={setSelectedGoalId}
                    selectedHistoryData={selectedHistoryData}
                  />
                ))
              )
            )}
            {/* Commented Older Goals Edit Button */}
            {/* Action buttons for save and cancel */}
            {/* {isEditable && (
            <div className={styles.btns_wrapper}>
              <Button
                id="cancelButton"
                onClick={handleClickCancel}
                label={t("btnCancel")}
                style="unfilled"
              />
              <Button
                id="saveButton"
                onClick={handleClickSave}
                label={t("btnSave")}
                style="filled"
              />
            </div>
          )} */}
          </>
        )}
    </div>
  );
};

export default Goals;
